import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  type IReactionDisposer,
} from "mobx";
import type { InjectionToken } from "tsyringe";
import { inject, injectable } from "tsyringe";
import {
  type IWarehouseApi,
  type RecentJournalFeedEntry,
  type RecentProductJournalFeedEntry,
  WAREHOUSE_API_TOKEN,
} from "../api/WarehouseApi";
import { Action } from "../types/entities";
import {
  REALTIME_STORE_TOKEN,
  type IRealtimeStore,
  type RealtimeFeedItem,
} from "./RealtimeStore";
import { SETTINGS_STORE_TOKEN, type ISettingsStore } from "./SettingsStore";
import {
  buildDefaultNotificationOptions,
  buildExpiringNotificationPayload,
  buildJournalNotificationPayload,
  buildLocalNotificationOptions,
  buildRealtimeMutationNotificationPayload,
  buildSystemTestNotificationPayload,
} from "./browser-notifications/payload-builder";
import {
  canUseStorage,
  getCurrentBrowserNotificationPermission,
  getPermissionLabel,
  getWebPushStatusLabel,
  isPushApiSupported,
  isSupportedNotificationPermission,
  type BrowserNotificationPermission,
  type WebPushSyncState,
} from "./browser-notifications/permission";
import {
  buildInitialReminder,
  buildNextReminder,
  isBoxEntityJournalEntry,
  isRecentProductJournalEntry,
  isReminderClearingAction,
  MAX_REMINDERS,
  normalizeFeedDateToMs,
  parseReminderSnapshot,
  REMINDERS_STORAGE_KEY,
  type PendingTakenReminder,
  type ReminderSnapshot,
} from "./browser-notifications/scheduler";
import {
  base64UrlToArrayBuffer,
  isPushSubscriptionBoundToKey,
  normalizeWebPushSendStats,
} from "./browser-notifications/subscription";

export interface IBrowserNotificationsStore {
  isSupported: boolean;
  isWebPushSupported: boolean;
  permission: BrowserNotificationPermission;
  permissionLabel: string;
  webPushStatusLabel: string;
  canSendTestWebPush: boolean;
  lastError: string | null;
  infoMessage: string | null;
  canRequestPermission: boolean;
  requestPermission: () => Promise<void>;
  sendTestWebPush: () => Promise<void>;
  start: () => void;
  stop: () => void;
}

export const BROWSER_NOTIFICATIONS_STORE_TOKEN = Symbol(
  "IBrowserNotificationsStore",
) as InjectionToken<IBrowserNotificationsStore>;

const RECENT_FEED_LIMIT = 30;
const FEED_REFRESH_THROTTLE_MS = 1200;

@injectable()
export class BrowserNotificationsStore implements IBrowserNotificationsStore {
  @observable
  permission: BrowserNotificationPermission = "unsupported";

  @observable
  lastError: string | null = null;

  @observable
  infoMessage: string | null = null;

  @observable
  private webPushState: WebPushSyncState = "idle";

  @observable
  private webPushError: string | null = null;

  @observable
  private webPushSyncInFlight = false;

  private cachedWebPushPublicKey: string | null = null;

  private readonly reminderTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private readonly reminders = new Map<string, PendingTakenReminder>();

  private readonly knownJournalIds = new Set<string>();

  private readonly disposers: IReactionDisposer[] = [];

  private started = false;

  private feedRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private feedRefreshInFlight = false;

  private lastFeedRefreshAt = 0;

  private notifyOnNextFeedRefresh = false;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(REALTIME_STORE_TOKEN)
    private readonly realtimeStore: IRealtimeStore,
    @inject(SETTINGS_STORE_TOKEN)
    private readonly settingsStore: ISettingsStore,
  ) {
    makeObservable(this);
    this.syncPermission();
  }

  @computed
  get isSupported(): boolean {
    return this.permission !== "unsupported";
  }

  @computed
  get isWebPushSupported(): boolean {
    return isPushApiSupported();
  }

  @computed
  get canRequestPermission(): boolean {
    return (
      this.isSupported &&
      this.permission !== "granted" &&
      this.permission !== "denied"
    );
  }

  @computed
  get permissionLabel(): string {
    return getPermissionLabel(this.permission);
  }

  @computed
  get webPushStatusLabel(): string {
    return getWebPushStatusLabel(this.webPushState, this.webPushError);
  }

  @computed
  get canSendTestWebPush(): boolean {
    return (
      this.settingsStore.webPushEnabled && this.webPushState === "subscribed"
    );
  }

  @computed
  private get shouldRunReminderTimers(): boolean {
    return (
      this.settingsStore.takenItemsRemindersEnabled &&
      this.shouldUseLocalBrowserNotifications
    );
  }

  @computed
  private get shouldUseLocalBrowserNotifications(): boolean {
    return (
      this.settingsStore.browserNotificationsEnabled &&
      (!this.settingsStore.webPushEnabled || this.webPushState !== "subscribed")
    );
  }

  @computed
  private get shouldEnableWebPush(): boolean {
    return (
      this.settingsStore.browserNotificationsEnabled &&
      this.settingsStore.webPushEnabled &&
      this.permission === "granted"
    );
  }

  @action.bound
  async requestPermission(): Promise<void> {
    this.syncPermission();

    if (
      !this.isSupported ||
      typeof Notification.requestPermission !== "function"
    ) {
      return;
    }

    try {
      const result = await Notification.requestPermission();
      runInAction(() => {
        this.permission = isSupportedNotificationPermission(result)
          ? result
          : "unsupported";
      });
    } catch (error) {
      runInAction(() => {
        this.lastError =
          error instanceof Error
            ? error.message
            : "Не удалось запросить разрешение на уведомления";
      });
    }
  }

  @action.bound
  async sendTestWebPush(): Promise<void> {
    runInAction(() => {
      this.lastError = null;
      this.webPushError = null;
      this.infoMessage = null;
    });

    try {
      if (!this.shouldEnableWebPush) {
        throw new Error("Web Push выключен или нет разрешения на уведомления");
      }

      this.showImmediateLocalNotification(
        "Проверка системных уведомлений",
        "Если вы видите это сообщение, системные уведомления браузера работают.",
        `webpush:probe:${Date.now()}`,
      );

      await this.subscribeWebPushIfNeeded();
      const rawStats = await this.api.sendWebPushTest();
      const stats = normalizeWebPushSendStats(rawStats);

      if (stats.delivered > 0) {
        await this.showSystemTestNotification(stats.delivered);
        runInAction(() => {
          this.infoMessage = `Тестовый push отправлен: доставлено ${stats.delivered}`;
        });
        return;
      }

      if (stats.expired > 0) {
        await this.subscribeWebPushIfNeeded();
        throw new Error(
          "Push-подписка устарела и была обновлена. Повторите тестовый push.",
        );
      }

      throw new Error(
        `Push не доставлен (delivered=${stats.delivered}, failed=${stats.failed}, expired=${stats.expired})`,
      );
    } catch (error) {
      runInAction(() => {
        this.infoMessage = null;
        this.webPushError =
          error instanceof Error
            ? error.message
            : "Не удалось отправить тестовый Web Push";
        this.lastError = this.webPushError;
      });
    }
  }

  @action.bound
  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.lastError = null;
    this.infoMessage = null;
    this.webPushError = null;
    this.syncPermission();
    this.webPushState = this.isWebPushSupported ? "idle" : "unsupported";
    this.hydrateReminders();
    if (this.shouldRunReminderTimers) {
      this.scheduleAllReminderTimers();
    }

    this.disposers.push(
      reaction(
        () => this.realtimeStore.events[0]?.id || "",
        () => {
          this.handleLatestRealtimeEvent();
        },
      ),
    );
    this.disposers.push(
      reaction(
        () => this.shouldRunReminderTimers,
        (enabled) => {
          if (enabled) {
            this.scheduleAllReminderTimers();
            return;
          }
          this.clearAllReminderTimers();
        },
      ),
    );
    this.disposers.push(
      reaction(
        () => [
          this.settingsStore.webPushEnabled,
          this.settingsStore.browserNotificationsEnabled,
          this.settingsStore.takenItemsRemindersEnabled,
          this.permission,
        ],
        () => {
          void this.syncWebPushSubscription();
        },
      ),
    );
    this.disposers.push(
      reaction(
        () => this.realtimeStore.status,
        () => {
          void this.syncWebPushSubscription();
        },
      ),
    );

    this.scheduleFeedRefresh(false);
    void this.syncWebPushSubscription();
  }

  @action.bound
  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.disposers.splice(0).forEach((dispose) => dispose());

    if (this.feedRefreshTimer) {
      clearTimeout(this.feedRefreshTimer);
      this.feedRefreshTimer = null;
    }

    this.clearAllReminderTimers();
  }

  private syncPermission(): void {
    this.permission = getCurrentBrowserNotificationPermission();
  }

  private handleLatestRealtimeEvent(): void {
    if (!this.started) {
      return;
    }

    const event = this.realtimeStore.events[0];
    if (!event) {
      return;
    }

    if (event.type === "product.expiring") {
      void this.showExpiringNotification(event);
      return;
    }

    if (
      event.type.startsWith("entity.") &&
      (event.entity === "products" || event.entity === "boxes")
    ) {
      void this.showRealtimeMutationNotification(event);
      return;
    }

    if (event.entity === "journals" && event.operation === "created") {
      this.scheduleFeedRefresh(true);
    }
  }

  private async showRealtimeMutationNotification(
    event: RealtimeFeedItem,
  ): Promise<void> {
    if (!this.settingsStore.browserNotificationsEnabled) {
      return;
    }

    this.syncPermission();
    if (this.permission !== "granted") {
      return;
    }

    if (
      typeof document !== "undefined" &&
      document.visibilityState !== "visible"
    ) {
      return;
    }

    const payload = buildRealtimeMutationNotificationPayload(event);
    await this.showNotification(payload.title, payload.body, payload.tag);
  }

  private async syncWebPushSubscription(): Promise<void> {
    if (!this.started) {
      return;
    }

    if (!this.isWebPushSupported) {
      runInAction(() => {
        this.webPushState = "unsupported";
      });
      return;
    }

    if (this.webPushSyncInFlight) {
      return;
    }

    this.webPushSyncInFlight = true;
    runInAction(() => {
      this.webPushError = null;
      this.lastError = null;
      this.webPushState = "syncing";
    });

    try {
      if (!this.shouldEnableWebPush) {
        await this.unsubscribeWebPushIfNeeded();
        runInAction(() => {
          this.webPushState = "disabled";
        });
        return;
      }

      await this.subscribeWebPushIfNeeded();
      runInAction(() => {
        this.webPushState = "subscribed";
      });
    } catch (error) {
      runInAction(() => {
        this.webPushError =
          error instanceof Error
            ? error.message
            : "Не удалось синхронизировать Web Push подписку";
        this.lastError = this.webPushError;
        this.webPushState = "error";
      });
    } finally {
      this.webPushSyncInFlight = false;
    }
  }

  private async subscribeWebPushIfNeeded(): Promise<void> {
    const registration = await this.getServiceWorkerRegistration();
    if (!registration || !("pushManager" in registration)) {
      throw new Error(
        "Service Worker с PushManager не найден. Проверьте HTTPS/localhost и доступность /service-worker.js",
      );
    }

    const publicKey = await this.getWebPushPublicKey(true);
    if (!publicKey) {
      throw new Error("Web Push не настроен на сервере (нет публичного ключа)");
    }

    let subscription = await registration.pushManager.getSubscription();
    if (
      subscription &&
      !isPushSubscriptionBoundToKey(subscription, publicKey)
    ) {
      try {
        await this.api.unsubscribeWebPush(subscription.endpoint);
      } catch {
        // Сервер мог уже потерять подписку.
      }

      try {
        await subscription.unsubscribe();
      } catch {
        // Ignore local unsubscribe errors and recreate subscription below.
      }
      subscription = null;
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToArrayBuffer(publicKey),
      });
    }

    const payload = subscription.toJSON();
    const endpoint = subscription.endpoint || payload.endpoint || "";
    const p256dh = payload.keys?.p256dh || "";
    const auth = payload.keys?.auth || "";

    if (!endpoint || !p256dh || !auth) {
      throw new Error("Браузер вернул неполную Push-подписку");
    }

    await this.api.subscribeWebPush({
      endpoint,
      expirationTime:
        typeof payload.expirationTime === "number"
          ? payload.expirationTime
          : null,
      remindersEnabled: this.settingsStore.takenItemsRemindersEnabled,
      keys: {
        p256dh,
        auth,
      },
    });
  }

  private async unsubscribeWebPushIfNeeded(): Promise<void> {
    const registration = await this.getServiceWorkerRegistration();
    if (!registration || !("pushManager" in registration)) {
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    const endpoint = subscription.endpoint;
    try {
      await this.api.unsubscribeWebPush(endpoint);
    } catch {
      // Сервер мог уже потерять подписку — локально все равно отписываемся.
    }

    await subscription.unsubscribe();
  }

  private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (
      typeof navigator === "undefined" ||
      !("serviceWorker" in navigator) ||
      typeof navigator.serviceWorker.getRegistration !== "function"
    ) {
      return null;
    }

    const existingRegistration =
      await navigator.serviceWorker.getRegistration();
    if (existingRegistration) {
      return this.ensureActiveServiceWorker(existingRegistration);
    }

    if (typeof navigator.serviceWorker.register !== "function") {
      return null;
    }

    try {
      const registration =
        await navigator.serviceWorker.register("/service-worker.js");
      return this.ensureActiveServiceWorker(registration);
    } catch {
      return null;
    }
  }

  private async ensureActiveServiceWorker(
    registration: ServiceWorkerRegistration,
  ): Promise<ServiceWorkerRegistration | null> {
    if (registration.active) {
      return registration;
    }

    if (typeof navigator.serviceWorker.ready?.then === "function") {
      try {
        const readyRegistration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), 8_000);
          }),
        ]);
        if (readyRegistration && readyRegistration.active) {
          return readyRegistration;
        }
      } catch {
        // Fallback to polling below.
      }
    }

    const startedAt = Date.now();
    while (Date.now() - startedAt < 8_000) {
      const refreshed = await navigator.serviceWorker.getRegistration();
      if (refreshed?.active) {
        return refreshed;
      }

      await new Promise<void>((resolve) => {
        setTimeout(resolve, 200);
      });
    }

    return null;
  }

  private async getWebPushPublicKey(
    forceRefresh = false,
  ): Promise<string | null> {
    if (!forceRefresh && this.cachedWebPushPublicKey) {
      return this.cachedWebPushPublicKey;
    }

    const response = await this.api.getWebPushPublicKey();
    if (!response.enabled || !response.publicKey) {
      return null;
    }

    this.cachedWebPushPublicKey = response.publicKey;
    return response.publicKey;
  }

  private scheduleFeedRefresh(notifyNew: boolean): void {
    if (!this.started) {
      return;
    }

    this.notifyOnNextFeedRefresh = this.notifyOnNextFeedRefresh || notifyNew;

    if (this.feedRefreshInFlight || this.feedRefreshTimer) {
      return;
    }

    const elapsed = Date.now() - this.lastFeedRefreshAt;
    const delay = Math.max(0, FEED_REFRESH_THROTTLE_MS - elapsed);

    this.feedRefreshTimer = setTimeout(() => {
      this.feedRefreshTimer = null;
      void this.refreshRecentFeed();
    }, delay);
  }

  private async refreshRecentFeed(): Promise<void> {
    if (!this.started || this.feedRefreshInFlight) {
      return;
    }

    this.feedRefreshInFlight = true;
    const notifyNew = this.notifyOnNextFeedRefresh;
    this.notifyOnNextFeedRefresh = false;

    try {
      const entries = await this.api.getRecentJournalFeed(RECENT_FEED_LIMIT);
      const currentIds = new Set(entries.map((entry) => entry.id));
      const newEntries = entries.filter(
        (entry) => !this.knownJournalIds.has(entry.id),
      );

      this.knownJournalIds.clear();
      currentIds.forEach((id) => this.knownJournalIds.add(id));

      const sortedNewEntries = [...newEntries].sort((left, right) => {
        return (
          normalizeFeedDateToMs(left.date) - normalizeFeedDateToMs(right.date)
        );
      });

      sortedNewEntries.forEach((entry) => {
        this.applyReminderDelta(entry);
      });

      if (notifyNew) {
        for (const entry of sortedNewEntries) {
          await this.showJournalNotification(entry);
        }
      }
    } catch (error) {
      runInAction(() => {
        this.lastError =
          error instanceof Error
            ? error.message
            : "Не удалось синхронизировать feed уведомлений";
      });
    } finally {
      this.feedRefreshInFlight = false;
      this.lastFeedRefreshAt = Date.now();
      if (this.notifyOnNextFeedRefresh) {
        this.scheduleFeedRefresh(false);
      }
    }
  }

  private applyReminderDelta(entry: RecentJournalFeedEntry): void {
    if (!isRecentProductJournalEntry(entry)) {
      return;
    }

    const productId = entry.productId.trim();
    if (!productId) {
      return;
    }

    if (entry.action === Action.take) {
      void this.syncReminderForTake(entry);
      return;
    }

    if (isReminderClearingAction(entry)) {
      this.clearReminder(productId);
    }
  }

  private async syncReminderForTake(
    entry: RecentProductJournalFeedEntry,
  ): Promise<void> {
    const productId = entry.productId.trim();
    if (!productId) {
      return;
    }

    try {
      const isBoxEntry = isBoxEntityJournalEntry(entry);
      const entity = isBoxEntry
        ? await this.api.getBoxById(productId)
        : await this.api.getProductById(productId);
      if (!entity || entity.parentId) {
        this.clearReminder(productId);
        return;
      }
    } catch {
      // При сетевой ошибке продолжаем по журналу.
    }

    this.startOrResetReminder(entry);
  }

  private startOrResetReminder(entry: RecentProductJournalFeedEntry): void {
    const productId = entry.productId.trim();
    if (!productId) {
      return;
    }

    this.reminders.set(productId, buildInitialReminder(entry));

    this.persistReminders();
    if (this.shouldRunReminderTimers) {
      this.scheduleReminderTimer(productId);
    }
  }

  private clearReminder(productId: string): void {
    const normalizedId = productId.trim();
    if (!normalizedId) {
      return;
    }

    const timer = this.reminderTimers.get(normalizedId);
    if (timer) {
      clearTimeout(timer);
      this.reminderTimers.delete(normalizedId);
    }

    if (this.reminders.delete(normalizedId)) {
      this.persistReminders();
    }
  }

  private scheduleAllReminderTimers(): void {
    this.reminders.forEach((_, productId) => {
      this.scheduleReminderTimer(productId);
    });
  }

  private clearAllReminderTimers(): void {
    this.reminderTimers.forEach((timer) => clearTimeout(timer));
    this.reminderTimers.clear();
  }

  private scheduleReminderTimer(productId: string): void {
    if (!this.shouldRunReminderTimers) {
      return;
    }

    const reminder = this.reminders.get(productId);
    if (!reminder) {
      return;
    }

    const previousTimer = this.reminderTimers.get(productId);
    if (previousTimer) {
      clearTimeout(previousTimer);
      this.reminderTimers.delete(productId);
    }

    const delay = Math.max(1000, reminder.nextAtMs - Date.now());
    const timer = setTimeout(() => {
      void this.handleReminderDue(productId);
    }, delay);
    this.reminderTimers.set(productId, timer);
  }

  private async handleReminderDue(productId: string): Promise<void> {
    const reminder = this.reminders.get(productId);
    if (!reminder) {
      return;
    }

    if (
      this.settingsStore.takenItemsRemindersEnabled &&
      this.shouldUseLocalBrowserNotifications
    ) {
      const isBoxReminder = productId.startsWith("box_");
      const title = isBoxReminder
        ? "Напоминание: коробка не возвращена"
        : "Напоминание: товар не возвращен";
      const body = isBoxReminder
        ? `Коробка "${reminder.productName}" все еще вне коробки "${reminder.sourceBoxName}".`
        : `Товар "${reminder.productName}" все еще вне коробки "${reminder.sourceBoxName}".`;
      await this.showNotification(title, body, `reminder:${productId}`);
    }

    this.reminders.set(productId, buildNextReminder(reminder));
    this.persistReminders();
    if (this.shouldRunReminderTimers) {
      this.scheduleReminderTimer(productId);
    }
  }

  private hydrateReminders(): void {
    if (!canUseStorage()) {
      return;
    }

    const snapshots = parseReminderSnapshot(
      window.localStorage.getItem(REMINDERS_STORAGE_KEY),
    );

    snapshots.forEach((snapshot) => {
      this.reminders.set(snapshot.productId, {
        productId: snapshot.productId,
        productName: snapshot.productName,
        sourceBoxId: snapshot.sourceBoxId,
        sourceBoxName: snapshot.sourceBoxName,
        nextAtMs: snapshot.nextAtMs,
        stepIndex: snapshot.stepIndex,
      });
    });
  }

  private persistReminders(): void {
    if (!canUseStorage()) {
      return;
    }

    const snapshot: ReminderSnapshot[] = Array.from(this.reminders.values())
      .slice(0, MAX_REMINDERS)
      .map((item) => ({
        productId: item.productId,
        productName: item.productName,
        sourceBoxId: item.sourceBoxId,
        sourceBoxName: item.sourceBoxName,
        nextAtMs: item.nextAtMs,
        stepIndex: item.stepIndex,
      }));

    try {
      window.localStorage.setItem(
        REMINDERS_STORAGE_KEY,
        JSON.stringify(snapshot),
      );
    } catch {
      // ignore storage quota/access errors
    }
  }

  private async showExpiringNotification(
    event: RealtimeFeedItem,
  ): Promise<void> {
    if (!this.shouldUseLocalBrowserNotifications) {
      return;
    }

    const payload = buildExpiringNotificationPayload(event);
    await this.showNotification(payload.title, payload.body, payload.tag);
  }

  private async showJournalNotification(
    entry: RecentJournalFeedEntry,
  ): Promise<void> {
    if (!this.shouldUseLocalBrowserNotifications) {
      return;
    }

    const payload = buildJournalNotificationPayload(entry);
    await this.showNotification(payload.title, payload.body, payload.tag);
  }

  private async showSystemTestNotification(delivered: number): Promise<void> {
    const payload = buildSystemTestNotificationPayload(delivered);

    const shown = this.showImmediateLocalNotification(
      payload.title,
      payload.body,
      payload.tag,
    );
    if (shown) {
      return;
    }

    await this.showNotification(payload.title, payload.body, payload.tag, true);
  }

  private showImmediateLocalNotification(
    title: string,
    body: string,
    tag: string,
  ): boolean {
    this.syncPermission();
    if (this.permission !== "granted" || typeof Notification === "undefined") {
      return false;
    }

    const options = buildLocalNotificationOptions(body, tag);

    try {
      new Notification(title, options);
      return true;
    } catch (error) {
      runInAction(() => {
        this.lastError =
          error instanceof Error
            ? error.message
            : "Не удалось показать локальное системное уведомление";
      });
      return false;
    }
  }

  private async showNotification(
    title: string,
    body: string,
    tag: string,
    preferServiceWorker = false,
  ): Promise<void> {
    this.syncPermission();

    if (this.permission !== "granted") {
      return;
    }

    const options = buildDefaultNotificationOptions(body, tag);

    if (
      typeof navigator !== "undefined" &&
      "serviceWorker" in navigator &&
      typeof navigator.serviceWorker.getRegistration === "function"
    ) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.showNotification(title, options);
          return;
        }
      } catch {
        // fallback to Notification constructor
      }
    }

    if (preferServiceWorker) {
      runInAction(() => {
        this.lastError =
          "Service Worker недоступен для системного уведомления (fallback отключен)";
      });
      return;
    }

    try {
      new Notification(title, options);
    } catch (error) {
      runInAction(() => {
        this.lastError =
          error instanceof Error
            ? error.message
            : "Не удалось показать браузерное уведомление";
      });
    }
  }
}
